"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { GalleryResponse, getAllTags, getGalleriesData } from "@/services/GalleryService";
import { initLightboxJS, SlideshowLightbox } from "lightbox.js-react";
import { Tag } from "@/common/entities/exhibits/Exhibit";
import LoadingExhibitions from "@/common/components/Loading/LoadingExhibitions";
import style from "./gallery.module.scss";
import Masonry from "react-masonry-css";
import "lightbox.js-react/dist/index.css";
import { CgClose } from "react-icons/cg";
import { GrPowerReset } from "react-icons/gr";
import { MdZoomOutMap } from "react-icons/md";
import { ImageWrapper } from "@/common/components/Images/ImageWrapper";
import GallerySearchFilter from "@/components/Gallery/GallerySearchFilter";
import { getActiveFilters,  } from "@/common/helpers/fetch.helpers";
import { debounce } from "lodash";
import { internalFilesBaseURL } from "@/lib/Constants";

const GalleryPageContent = () => {
    const [exhibits, setExhibits] = useState<GalleryResponse | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [showFilters, setShowFilters] = useState<boolean>(true);
    const [tags, setTags] = useState<Tag[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [from, setFrom] = useState(0);
    const [size] = useState(30);
    const [total, setTotal] = useState(0);
    const observer = useRef<IntersectionObserver | null>(null);
    const [sticky, setSticky] = useState<boolean>(false);
    const [hoveredTagIndex, setHoveredTagIndex] = useState<number | null>(null);
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();
    const [resetFilterTrigger, setResetFilterTrigger] = useState<boolean>(false);
    const [searchGallery, setSearchGallery] = useState<string>(searchParams.get("searchModel") || "");
    const [openLightbox, setOpenLightbox] = useState<boolean>(false);
    const [lightboxImages, setLightboxImages] = useState<{ src: string; alt: string }[]>([]);

    const breakpointColumnsObj = {
        default: 4,
        1100: 2,
        700: 1,
    };

    const debounceFetchData = useCallback(
        debounce((filters) => {
            fetchData(filters);
        }, 300),
        []
    );

    const fetchData = useCallback(
        async (filters: any) => {
            await fetchTags();
            setLoading(true);
            try {
                const result = await getGalleriesData(filters);
                if (filters.from === 0) {
                    setExhibits(result);
                    setLightboxImages(
                        result.data.map((exhibit) => ({
                            src: internalFilesBaseURL + exhibit.image.originalFileName,
                            alt: exhibit.image.altName,
                        }))
                    );
                } else {
                    setExhibits((prev) => ({
                        ...result,
                        data: [...(prev?.data || []), ...result.data],
                    }));
                    setLightboxImages((prevData) => [
                        ...(prevData || []),
                        ...result.data.map((exhibit) => ({
                            src: internalFilesBaseURL + exhibit.image.originalFileName,
                            alt: exhibit.image.altName,
                        })),
                    ]);
                }

                setTotal(result.total);
            } catch {
                setExhibits(undefined);
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        const fromParam = searchParams.get("from");
        if (fromParam) {
            setFrom(parseInt(fromParam, 10));
        }
    }, [searchParams]);


    useEffect(() => {
        initLightboxJS("6B2F-9B71-105E-2498", "team");
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setSticky(window.scrollY > 175);
        };
        window.addEventListener("scroll", handleScroll, true);

        return () => {
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, []);

    const handleTagChange = (tagId: string) => {
        setSelectedTags((prevTags) => {
            if (prevTags.includes(tagId)) {
                return prevTags.filter((id) => id !== tagId);
            } else {
                return [...prevTags, tagId];
            }
        });

        const filters = getActiveFilters(searchParams);
        const ids = filters.tagsIds?.split(",") || [];
        const newIds = ids.includes(tagId.toString())
            ? ids.filter((id) => id !== tagId.toString())
            : [...ids, tagId.toString()];

        const params = new URLSearchParams(searchParams.toString());
        if (newIds) {
            params.set("tagsIds", newIds.join(","));
            setFrom(0);
        } else {
            params.delete("searchModel");
        }

        router.push(`${pathname}?${params.toString()}`);
        debounceFetchData({ ...getActiveFilters(searchParams), from: 0, size });
    };

    const fetchTags = async () => {
        const tagsResult = await getAllTags();
        setTags(tagsResult);
    };

    const onSearchFilter = (search: any) => {
        setSearchGallery(search);
        setResetFilterTrigger(false);
        const params = new URLSearchParams(searchParams.toString());
        if (search) {
            params.set("searchModel", search);
            setFrom(0);
        } else {
            params.delete("searchModel");
        }
        router.push(`${pathname}?${params.toString()}`);
        debounceFetchData({ ...getActiveFilters(searchParams), from: 0, size });
    };

    useEffect(() => {
        const params = {
            ...getActiveFilters(searchParams),
            from,
            size,
        };
        debounceFetchData(params);
    }, [from, searchParams]);

    const resetFilters = () => {
        setSearchGallery("");
        setSelectedTags([]);
        setResetFilterTrigger(true);
        setFrom(0);
        const params = new URLSearchParams(searchParams.toString());
        params.delete("searchModel");
        params.delete("tagsIds");
        router.push(`${pathname}?${params.toString()}`);
        fetchData({ from: 0, size });
    };

    const ExhibitCards = exhibits?.data?.map((exhibit, index) => (
        <div
            key={`${exhibit.id}-${index}`}
            className={style.wrapperImage}
            role="button"
            tabIndex={0}
            onMouseEnter={() => setHoveredTagIndex(index)}
            onMouseLeave={() => setHoveredTagIndex(null)}
            onClick={() => setOpenLightbox(true)}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    setOpenLightbox(true);
                }
            }}
        >
            <div className={style.imageSection}>
                <ImageWrapper url={exhibit.image.originalFileName} title={exhibit.image.altName} width={500} height={500} />
                <div className={`${style.imageOverlay} ${hoveredTagIndex === index ? style.overlayVisible : ""}`}></div>
                <div className={`${style.tagSection} ${hoveredTagIndex === index ? style.tagsVisible : ""}`}>
                    <Container fluid>
                        <Row>
                            <Col xs={3}>
                                <MdZoomOutMap className={style.zoomIcon} />
                            </Col>
                            <Col xs={9} className="text-end">
                                {exhibit.tags.map((tag, tagIndex) => (
                                    <div key={tagIndex} className={style.tagsWrapper}>
                                        <div className={style.tags}>{tag.name}</div>
                                    </div>
                                ))}
                            </Col>
                        </Row>
                    </Container>
                </div>
            </div>
            <div className={style.title}>{exhibit.title}</div>
            <div className={style.description}>{exhibit.shortDescription}</div>
        </div>
    ));

    const lastElementRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (loading || from >= total) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        setFrom((prevFrom) => prevFrom + size);
                    }
                },
            );

            if (node) observer.current.observe(node);
        },
        [loading, total],
    );

    return (
        <div className={style.exhibitsLayout}>
            <Container className="my-4">
                <h1 className="sectionTitle">Galerie foto</h1>
                <p className={style.description}>
                    Avem placerea de a va prezenta o parte din modelele de seminee executate de noi la comanda, in functie de preferintele clientilor nostri.
                </p>
            </Container>

            <div className={style.headerImage}></div>
            {!showFilters && (
                <Container>
                    <Button className={style.filterButton} onClick={() => setShowFilters(!showFilters)}>
                        Filtreaza
                    </Button>
                </Container>
            )}
            {showFilters && (
                <div className={`${style.exhibitsFilters} ${sticky ? style.sticky : ""}`}>
                    <Container>
                        <Row>
                            <Col>
                                <div className={style.filterText}>
                                    <div>Filtreaza:</div>
                                    <div className={style.filterClose}>
                                        <CgClose onClick={() => setShowFilters(false)} />
                                    </div>
                                </div>
                                {tags &&
                                    tags.map((tag: Tag) => (
                                        <Button
                                            onClick={() => handleTagChange(tag.id)}
                                            className={`${style.item} ${selectedTags.includes(tag.id) ? style.active : ""}`}
                                            key={tag.id}
                                            variant="default"
                                        >
                                            {tag.name}
                                        </Button>
                                    ))}
                                {selectedTags?.length > 0 && (
                                    <div className={style.resetButton}>
                                        <GrPowerReset onClick={resetFilters} />
                                    </div>
                                )}
                                <div className={style.filterText}>
                                    <GallerySearchFilter onSearchFilter={onSearchFilter} resetFilters={resetFilterTrigger}/>
                                    {searchGallery && (
                                        <div className={style.resetButtonGallery}>
                                            <GrPowerReset onClick={resetFilters} />
                                        </div>
                                    )}
                                </div>
                            </Col>

                        </Row>
                    </Container>
                </div>
            )}
            <Container className={style.masonryLayout}>
                {loading && from === 0 ? (
                    <LoadingExhibitions withSpinner={true} rows={4} cols={4} colSizes={{ xs: 12, sm: 6, md: 6, lg: 3, xl: 3 }} />
                ) : (
                    <Masonry breakpointCols={breakpointColumnsObj} className={style.my_masonry_grid} columnClassName={style.my_masonry_grid_column}>
                        {ExhibitCards}
                        <div ref={lastElementRef} style={{ height: "20px" }}></div>
                    </Masonry>
                )}
            </Container>
            <SlideshowLightbox
                theme="lightbox"
                images={lightboxImages}
                open={openLightbox}
                lightboxIdentifier="lbox1"
                modalClose="clickOutside"
                startingSlideIndex={hoveredTagIndex ?? undefined}
                onClose={() => {
                    setOpenLightbox(false);
                }}
            ></SlideshowLightbox>
        </div>
    );
};

export default GalleryPageContent;
